import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
// import { v4 as uuidv4 } from 'uuid'; // Unused
import SlateEditor from '@common/components/tasks/extraData/lazy-slate';
import { namespace } from '@common/config/constants';
import { Reminder } from '@common/models/reminder';
import { useAppSelector } from '@common/reducers/hooks/store.hook';
import {
    closeEditor,
    selectEditingReminder,
    selectRelatedContext
} from '@common/reducers/RemindersUIReducer';
import { createReminderThunk, patchReminderThunk } from '@common/reducers/reminders-thunks';
import { AppThunkDispatch } from '@common/types/common-root-state-type';
import BTDatePicker from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-date-picker";
import BTTimePicker from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-time-picker";
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Descendant } from 'slate';
import { selectMergedContactMap } from '@common-reducers/WhatsAppReducer';
import { getWaChatContactDisplayName } from '@common/utils/display-name-utils';
import { fetchAndCacheChatNameThunk, fetchAndCacheProfilePicUrlsThunk } from '@extension-context-content/thunks/WhatsappThunks';

const ReminderEditor: React.FC = () => {
    const { t } = useTranslation(namespace);
    const dispatch = useDispatch<AppThunkDispatch>();

    // Get state from Redux
    const initialReminder = useAppSelector(selectEditingReminder);
    const relatedContext = useAppSelector(selectRelatedContext);

    // Extract related context
    const relatedMessageId = relatedContext.messageId;
    const relatedMessageText = relatedContext.messageText;
    const relatedChatId = relatedContext.chatId;
    const relatedChatDisplayName = relatedContext.chatDisplayName;
    const mergedContactMap = useAppSelector(selectMergedContactMap);
    const cachedContact = relatedChatId ? mergedContactMap[relatedChatId] : undefined;
    const resolvedChatNameFromMap = relatedChatId
        ? getWaChatContactDisplayName(cachedContact, { fallback: '' })
        : '';
    const cachedExt = cachedContact as { imgUrl?: string; profilePicLastUpdate?: string } | undefined;
    const cachedImgUrl = cachedExt?.imgUrl;
    const cachedPicLastUpdate = cachedExt?.profilePicLastUpdate;
    useEffect(() => {
        if (!relatedChatId) return;
        if (!cachedContact?.name) dispatch(fetchAndCacheChatNameThunk(relatedChatId));
        if (!cachedImgUrl && !cachedPicLastUpdate) {
            dispatch(fetchAndCacheProfilePicUrlsThunk(relatedChatId));
        }
    }, [dispatch, relatedChatId, cachedContact?.name, cachedImgUrl, cachedPicLastUpdate]);
    const isUnknownChatPlaceholder =
        !relatedChatDisplayName ||
        relatedChatDisplayName === 'Unknown Contact' ||
        relatedChatDisplayName === relatedChatId;
    const displayChatName = isUnknownChatPlaceholder
        ? (resolvedChatNameFromMap || relatedChatDisplayName || relatedChatId?.split('@')[0])
        : relatedChatDisplayName;

    // Log received props
    useEffect(() => {
        console.log('[reminder-message-logs] ReminderEditor received props:', {
            hasInitialReminder: !!initialReminder,
            relatedMessageId,
            relatedMessageText: relatedMessageText?.substring(0, 50),
            relatedMessageTextLength: relatedMessageText?.length || 0,
            relatedChatId,
            relatedChatDisplayName
        });
    }, [initialReminder, relatedMessageId, relatedMessageText, relatedChatId, relatedChatDisplayName]);

    const [title, setTitle] = useState(initialReminder?.title || '');

    // Build the initial description for a NEW reminder.
    //
    // IMPORTANT: do NOT include relatedContext.messageText here. The dialog
    // already renders the source message as a quoted preview at the top of
    // the form, so duplicating it in the description shows the same text
    // twice. We DO put media (images / videos) here because the preview
    // panel is text-only and can't render them.
    const buildInitialDescriptionFromContext = (): Descendant[] => {
        const blocks: Descendant[] = [];
        const imageUrls = Array.isArray(relatedContext.imageUrls) ? relatedContext.imageUrls : [];
        const videoUrls = Array.isArray(relatedContext.videoUrls) ? relatedContext.videoUrls : [];

        const pushVoid = (node: Descendant) => {
            blocks.push(node);
            // Slate requires a non-void block after a void node.
            blocks.push({ type: 'paragraph', children: [{ text: '' }] } as Descendant);
        };
        for (const url of imageUrls) {
            if (typeof url === 'string' && url) pushVoid({ type: 'image', url, children: [{ text: '' }] } as Descendant);
        }
        for (const url of videoUrls) {
            if (typeof url === 'string' && url) pushVoid({ type: 'video', url, children: [{ text: '' }] } as Descendant);
        }
        if (blocks.length === 0) {
            blocks.push({ type: 'paragraph', children: [{ text: '' }] } as Descendant);
        }
        return blocks;
    };

    const [description, setDescription] = useState<Descendant[]>(
        initialReminder?.description || buildInitialDescriptionFromContext()
    );

    // Local state for Date and Time
    const [dateStr, setDateStr] = useState('');
    const [timeStr, setTimeStr] = useState('');

    // Initialize date/time from initialReminder or default to now
    useEffect(() => {
        let targetDate = moment();

        if (initialReminder?.dueAt) {
            targetDate = moment(initialReminder.dueAt);
        } else {
            targetDate = moment();
        }

        if (targetDate.isValid()) {
            setDateStr(targetDate.format('DD/MM/YYYY'));
            setTimeStr(targetDate.format('HH:mm'));
        }
    }, [initialReminder]);

    const [localRelatedMessageText] = useState(initialReminder?.relatedMessageText || relatedMessageText);

    const handleSave = async () => {
        if (!title.trim()) {
            toast.error(t('reminders.enterTitle', 'Enter Title to Reminder'));
            return;
        }

        // Parse dateStr and timeStr back to timestamp
        // dateStr: dd/mm/yyyy, timeStr: HH:mm
        let dueAt = Date.now(); // Default fallback

        if (dateStr && timeStr) {
            const dateParts = dateStr.split('/');
            const timeParts = timeStr.split(':');

            if (dateParts.length === 3 && timeParts.length === 2) {
                const day = parseInt(dateParts[0], 10);
                const month = parseInt(dateParts[1], 10) - 1; // 0-indexed
                const year = parseInt(dateParts[2], 10);
                const hours = parseInt(timeParts[0], 10);
                const minutes = parseInt(timeParts[1], 10);

                const m = moment({ year, month, day, hour: hours, minute: minutes });
                if (m.isValid()) {
                    dueAt = m.valueOf();
                }
            }
        }

        const newReminder: Reminder = {
            _id: initialReminder?._id, // Use _id for DBService
            // id: initialReminder?.id || uuidv4(), // DBService uses _id
            title,
            description,
            dueAt,
            relatedMessageId: initialReminder?.relatedMessageId || relatedMessageId,
            relatedMessageText: localRelatedMessageText,
            relatedChatId: initialReminder?.relatedChatId || relatedChatId,
            relatedChatDisplayName: initialReminder?.relatedChatDisplayName || relatedChatDisplayName,
            isActive: true,
            // createdAt: initialReminder?.createdAt || Date.now(), // DBService handles this usually
        } as any; // Cast to any to avoid strict BaseEntity checks if needed

        try {
            if (initialReminder?._id) {
                await dispatch(patchReminderThunk({
                    reminder: newReminder,
                    options: { ignoreOriginalEntity: false }
                })).unwrap();
            } else {
                await dispatch(createReminderThunk({ reminder: newReminder })).unwrap();
            }

            toast.success(t('reminders.savedSuccess', 'Reminder saved successfully'));
            dispatch(closeEditor());
        } catch (error) {
            // Error handling (including popup display) is done in the thunk
            console.error('[reminder-message-logs] Error saving reminder:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            toast.error(errorMessage);
            dispatch(closeEditor());
        }
    };

    const handleCancel = () => {
        dispatch(closeEditor());
    };

    return (
        <>
            {/* Header Decoration */}
            <div className="h-2 w-full" style={{ background: 'var(--fab-bg)' }} />

            {/* Header */}
            <div className="px-8 pt-8 pb-4">
                <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white capitalize leading-tight">
                    {initialReminder ? t('reminders.editReminder', 'Edit Reminder') : t('reminders.createReminder', 'Create a Reminder')}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                    {t('reminders.reminderDescription', 'Once a Reminder is set, Blueticks will notify you at your chosen time.')}
                </p>
            </div>

            {/* Content */}
            <div className="px-8 py-2 space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="grid w-full items-center gap-1.5">
                    <Label htmlFor="title">{t('reminders.title', 'Title')}</Label>
                    <Input
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('reminders.titlePlaceholder', 'e.g. Follow up with client')}
                        className="bg-[var(--color-surface)] border-slate-200 dark:border-slate-700"
                    />
                </div>

                {(localRelatedMessageText || relatedChatId) && (
                    <div className="p-3 bg-[rgba(0,0,0,0.05)] rounded-md border-l-4 border-green-500">
                        <div className="flex items-center gap-2 mb-2">
                            {relatedChatId && (
                                <ContactAvatar
                                    contactId={relatedChatId}
                                    size={24}
                                    avatarStyle={{ width: '24px', height: '24px' }}
                                />
                            )}
                            <span className="text-sm font-medium text-foreground">
                                {displayChatName || t('reminders.unknownContact', 'Unknown Contact')}
                            </span>
                        </div>

                        {localRelatedMessageText && (
                            <>
                                <span className="text-xs text-muted-foreground block mb-1">{t('reminders.relatedMessage', 'Related Message')}:</span>
                                <p className="text-sm italic text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto" dir="auto">"{localRelatedMessageText}"</p>
                            </>
                        )}
                    </div>
                )}

                <div>
                    <Label className="mb-2 block">{t('reminders.dateAndTime', 'Date & Time')}</Label>
                    <div className="flex items-center gap-2">
                        <BTDatePicker
                            value={dateStr}
                            onChange={setDateStr}
                        />
                        <BTTimePicker
                            value={timeStr}
                            onChange={setTimeStr}
                        />
                    </div>
                </div>

                <div className="grid w-full gap-1.5">
                    <Label htmlFor="reminder-editor">{t('reminders.descriptionOptional', 'Description (optional)')}</Label>
                    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-2 min-h-[100px] focus-within:ring-1 focus-within:ring-slate-400">
                        <SlateEditor
                            value={description}
                            onValueUpdate={(val) => setDescription(Array.isArray(val) ? val : val.editorValue)}
                            addBorder={false}
                            id="reminder-editor"
                            hideFooter={true}
                        />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="px-8 pb-8 pt-4 flex flex-col sm:flex-row gap-3 justify-end">
                <Button variant="outline" onClick={handleCancel} className="rounded-xl h-11 border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors font-medium">
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    className="rounded-xl h-11 text-white font-bold shadow-lg transition-all hover:scale-[1.01] active:scale-[0.99] hover:opacity-90"
                    style={{ background: 'var(--fab-bg)', color: 'var(--fab-text)' }}
                >
                    {initialReminder ? t('common.update', 'Update') : t('common.save', 'Save')}
                </Button>
            </div>
        </>
    );
};

export default ReminderEditor;
